/**
 * Precepi brand mark — logo + name on a single line, linking home.
 *
 * - ``size="lg"`` — prominent hero variant for the Keyword home (the default
 *   landing surface); pass ``tagline`` to render the tagline beneath.
 * - ``size="sm"`` — compact variant used as lightweight page branding on the
 *   Cogni and Investigator surfaces. Returns just the inline mark so the
 *   caller controls alignment.
 *
 * Inside the shell a plain click focuses (or opens) the Home tab: a real
 * navigation to `/` would reload, and the shell restores the previously
 * active tab, so the click looked dead. `href` stays for Cmd/middle-click.
 */
import React, { useContext } from 'react';
import { ShellContext } from '../shell/shellContext';

interface BrandMarkProps {
  size?: 'sm' | 'lg';
  /** Render the tagline beneath the mark (hero use only). */
  tagline?: boolean;
}

const BrandMark: React.FC<BrandMarkProps> = ({ size = 'sm', tagline = false }) => {
  const isLg = size === 'lg';
  const shell = useContext(ShellContext);
  const goHome = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!shell || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    shell.openTab('/');
  };
  const mark = (
    <a
      href="/"
      onClick={goHome}
      className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity"
    >
      <img
        src="/logo.svg"
        alt=""
        aria-hidden="true"
        className={isLg ? 'h-10 w-12' : 'h-[18px] w-[22px]'}
      />
      <span
        className={`font-display font-semibold tracking-[-0.02em] text-ink ${
          isLg ? 'text-5xl' : 'text-[19px]'
        }`}
      >
        Precepi
      </span>
    </a>
  );

  if (!isLg && !tagline) return mark;

  return (
    <div className={isLg ? 'text-center' : ''}>
      {mark}
      {tagline && (
        <p className="text-sm md:text-base font-medium text-slate-500 tracking-widest uppercase mt-2">
          AI powered precedent search for strategic labeling
        </p>
      )}
    </div>
  );
};

export default BrandMark;
